import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { LeaveEditingModal } from '../../Modal/LeaveEditingModal';
import { LaterTextEditor } from './LaterTextEditor';
import { EmergentTextEditor } from './EmergentTextEditor';
import { MainContentHeader } from '../../MainContentHeader';
import { CrossButton } from '../../Button';
import { Wrapper } from '../../Wrapper';
import { StyledTextEditorModal } from '../../Styled';
import {
  LaterExemplar,
  EmergentExemplar,
  ExemplarCombined,
  WordsAndSentencesContent,
  EmergentContentSection,
  Side,
  Section,
  Image,
  ProcedureContentLine,
  ExemplarWord,
  SeparatedStyledWord,
  FilteredLaterTextSection,
  FilteredEmergentTextSection,
  FilteredProcedureSection,
} from '../../../types';
import { COLORS, EMERGENT } from '../../../constants';
import { getStageColors } from '../../../utils';
import { isEqual } from 'lodash';

interface ITextEditor {
  exemplar: ExemplarCombined;
  defaultExemplar: ExemplarCombined;
  stage: string;
  page: string;
  onClose: () => void;
  onTextEdited: (exemplar: ExemplarCombined) => void;
}

export const TextEditor: React.FC<ITextEditor> = props => {
  const { exemplar, defaultExemplar, stage, page, onClose, onTextEdited } = props;

  const { text_type } = exemplar as EmergentExemplar | LaterExemplar;

  const [editedExemplar, setEditedExemplar] = useState<ExemplarCombined>(exemplar);

  const [isLeaveModalShown, setLeaveModal] = useState(false);

  const { main: mainColor, label: labelBgColor } = getStageColors(stage);

  const getEmergentSectionWordsLines = (
    content: WordsAndSentencesContent[],
    page: string
  ): FilteredEmergentTextSection[] => {
    const pageContent = content.find(({ label }) => label.toLowerCase().replace(/\s/g, '-') === page)?.content;
    const allContent = pageContent
      ? ([...pageContent.left, ...pageContent.right].filter(({ type }) => type !== 'image') as EmergentContentSection[])
      : [];

    return allContent.map(({ lines, style }) => ({
      lines: lines.map(({ words }) => ({
        words: words.map(({ text, style }) => ({
          text,
          style,
        })),
      })),
      style,
    }));
  };

  const getLaterSectionWordsLines = (content: Side): string => {
    const textSections = ([...content.left, ...content.right] as Array<Section | Image>).filter(
      ({ type }) => type !== 'image'
    ) as Section[];

    const filteredTextSections: FilteredLaterTextSection[] = textSections.map(({ lines }) => ({
      lines: lines.map(({ words }) => ({
        words: words.map(({ text, style }) =>
          style
            ? {
                text,
                style,
              }
            : { text }
        ),
      })),
    }));

    const getSectionWithWordsLines = (section: FilteredLaterTextSection) => {
      return section.lines.reduce(
        (line, { words }) => {
          const wordsLine = words.reduce((word, { text, style }, index) => {
            const styles = style ? style.reduce((styles, currentStyle) => styles + currentStyle, '') : '';
            if (index === words.length - 1) {
              return word + text + styles;
            } else {
              return word + text + styles + ' ';
            }
          }, '');

          return [...line, wordsLine];
        },
        ['']
      );
    };

    const sectionsWithWordsLines = filteredTextSections.reduce(
      (sections, currentSection) => sections + getSectionWithWordsLines(currentSection),
      ''
    );

    return sectionsWithWordsLines;
  };

  const getProcedureLaterSectionWordsLines = (content: Side) => {
    const textSections = ([...content.left, ...content.right] as Array<Section | Image>).filter(
      ({ type }) => type !== 'image'
    ) as Section[];

    const getSeparatedStyledWords = (words: ExemplarWord[]): SeparatedStyledWord[] => {
      const modifiedLine = [];

      for (let i = 0; i < words.length; i++) {
        if (words[i].text.match(/\s/g)) {
          const wordsStyle = words[i].style;
          const wordsArray = words[i].text.split(' ');

          wordsArray.forEach(wordText => {
            modifiedLine.push(
              wordsStyle && wordsStyle[0] !== 'nospacing'
                ? {
                    text: wordText,
                    style: wordsStyle,
                  }
                : { text: wordText }
            );
          });
        } else {
          modifiedLine.push(
            words[i].style
              ? {
                  text: words[i].text,
                  style: words[i].style,
                }
              : {
                  text: words[i].text,
                }
          );
        }
      }

      return modifiedLine;
    };

    const filteredTextSections = textSections.map(({ lines, style }) => {
      const wordsLines = (lines as ProcedureContentLine[]).map(({ words, style }) => {
        const wordsLine = getSeparatedStyledWords(words);
        return style && style?.length > 0
          ? {
              words: wordsLine,
              style,
            }
          : {
              words: wordsLine,
            };
      });

      return style && style.length > 0
        ? {
            lines: wordsLines,
            style,
          }
        : {
            lines: wordsLines,
          };
    });

    const getSectionWithWordsLines = (section: FilteredProcedureSection) => {
      return section.lines.reduce(
        (line, { words, style }) => {
          const wordsLine = words.reduce(
            (word, { text, style }, index) => {
              const updatedText = index === words.length - 1 ? text : text + ' ';
              const updatedWord =
                style && style.length > 0
                  ? {
                      text: updatedText,
                      style,
                    }
                  : {
                      text: updatedText,
                    };

              return [...word, updatedWord];
            },
            [{}]
          );

          return style && style.length > 0
            ? [
                ...line,
                {
                  wordsLine,
                  style,
                },
              ]
            : [
                ...line,
                {
                  wordsLine,
                },
              ];
        },
        [{}]
      );
    };

    const sectionsWithWordsLines = filteredTextSections.reduce(
      (sections, currentSection) => [...sections, getSectionWithWordsLines(currentSection)],
      [{}]
    );

    return sectionsWithWordsLines;
  };

  const isDefaultChanged = useMemo(() => {
    let defaultExemplarSectionWordsLines;
    let updatedExemplarSectionWordsLines;

    if (stage === 'emergent') {
      defaultExemplarSectionWordsLines = getEmergentSectionWordsLines(
        exemplar?.content as WordsAndSentencesContent[],
        page
      );
      updatedExemplarSectionWordsLines = getEmergentSectionWordsLines(
        editedExemplar?.content as WordsAndSentencesContent[],
        page
      );
    } else {
      if (text_type === 'Procedure') {
        defaultExemplarSectionWordsLines = getProcedureLaterSectionWordsLines(exemplar?.content as Side);
        updatedExemplarSectionWordsLines = getProcedureLaterSectionWordsLines(editedExemplar?.content as Side);
      } else {
        defaultExemplarSectionWordsLines = getLaterSectionWordsLines(exemplar?.content as Side);
        updatedExemplarSectionWordsLines = getLaterSectionWordsLines(editedExemplar?.content as Side);
      }
    }

    return !isEqual(defaultExemplarSectionWordsLines, updatedExemplarSectionWordsLines);
  }, [editedExemplar]);

  const updateEditedExemplar = (editedExemplar: ExemplarCombined) => {
    setEditedExemplar(editedExemplar);
  };

  const handleSave = () => {
    onTextEdited(editedExemplar);
    onClose();
  };

  const handleClose = () => {
    if (isDefaultChanged) {
      setLeaveModal(true);
      return;
    }

    onClose();
  };

  const renderEditor = () => {
    if (stage === EMERGENT) {
      return (
        <EmergentTextEditor
          exemplar={exemplar as EmergentExemplar}
          defaultExemplar={defaultExemplar as EmergentExemplar}
          mainColor={mainColor}
          page={page}
          updateEditedExemplar={updateEditedExemplar}
          onSave={handleSave}
        />
      );
    }

    return (
      <LaterTextEditor
        exemplar={exemplar as LaterExemplar}
        defaultExemplar={defaultExemplar as LaterExemplar}
        mainColor={mainColor}
        page={page}
        updateEditedExemplar={updateEditedExemplar}
        onSave={handleSave}
      />
    );
  };

  return createPortal(
    <StyledTextEditorModal data-testid="text-editor">
      <CrossButton
        ariaLabel="close text editor"
        visible={true}
        top={31}
        right={40}
        width={46}
        height={46}
        onClick={() => {
          handleClose();
        }}
      />
      {isLeaveModalShown && (
        <LeaveEditingModal
          stage={stage}
          isTextEditor
          onUpdate={() => {
            return;
          }}
          onClose={() => {
            setLeaveModal(false);
          }}
          onConfirmLeaving={() => {
            setLeaveModal(false);
            onClose();
          }}
          onSave={() => {
            setLeaveModal(false);
          }}
          isSavedExemplar={false}
        />
      )}
      <Wrapper color={COLORS.blackOlive}>
        <MainContentHeader headerText={page} showHeading={false} showShadow={false} labelBgColor={labelBgColor} />
        {renderEditor()}
      </Wrapper>
    </StyledTextEditorModal>,
    document.getElementsByTagName('body')[0] as HTMLElement
  );
};
